// G2.10 — Per-notification-type channel preferences matrix (admin app).
//
// Rows = notification types. Columns = delivery channels. Each cell is a
// toggle that lazily creates a row on first click. A column is visually
// locked when its flat master switch (shown in the card above) is off.
//
// Autosave: optimistic UI → PATCH → revert + toast on error.
// Reset: DELETE /preferences/matrix → reload.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ConfirmDialog, Skeleton, useToast } from '@bbpos/ui';
import {
  notificationService,
  type NotificationChannel,
  type NotificationPreferenceMatrixRow,
  type NotificationPreferences,
  type NotificationType,
} from '@/services/notification.service';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, cn } from '@/constants/design-tokens';

// ─── Static config ──────────────────────────────────────────────────────────

interface ChannelMeta {
  key: NotificationChannel;
  label: string;
  tone: 'brand' | 'indigo' | 'teal' | 'emerald' | 'violet';
  globalKey: keyof NotificationPreferences | null;
  iconPath: string;
}

const CHANNELS: ChannelMeta[] = [
  { key: 'in_app', label: 'In-App', tone: 'brand', globalKey: null,
    iconPath: 'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0' },
  { key: 'email', label: 'Email', tone: 'indigo', globalKey: 'email_enabled',
    iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  { key: 'sms', label: 'SMS', tone: 'teal', globalKey: 'sms_enabled',
    iconPath: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' },
  { key: 'whatsapp', label: 'WhatsApp', tone: 'emerald', globalKey: 'whatsapp_enabled',
    iconPath: 'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z' },
  { key: 'push', label: 'Push', tone: 'violet', globalKey: 'push_enabled',
    iconPath: 'M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0a3 3 0 11-6 0' },
];

const TONE_ON: Record<ChannelMeta['tone'], string> = {
  brand: 'bg-brand-500/15 text-brand-700 ring-brand-500/40 dark:text-brand-300',
  indigo: 'bg-indigo-500/15 text-indigo-700 ring-indigo-500/40 dark:text-indigo-300',
  teal: 'bg-teal-500/15 text-teal-700 ring-teal-500/40 dark:text-teal-300',
  emerald: 'bg-emerald-500/15 text-emerald-700 ring-emerald-500/40 dark:text-emerald-300',
  violet: 'bg-violet-500/15 text-violet-700 ring-violet-500/40 dark:text-violet-300',
};

interface TypeRow {
  type: NotificationType;
  label: string;
}

const TYPE_ROWS: TypeRow[] = [
  { type: 'LOW_STOCK', label: 'Low Stock' },
  { type: 'BACKUP_COMPLETE', label: 'Backup Complete' },
  { type: 'SHIFT_REMINDER', label: 'Shift Reminder' },
  { type: 'ORDER_NEW', label: 'New Order' },
  { type: 'ORDER_UPDATE', label: 'Order Update' },
  { type: 'SYNC_CONFLICT', label: 'Sync Conflict' },
  { type: 'REPORT_READY', label: 'Report Ready' },
  { type: 'EXPENSE_APPROVAL', label: 'Expense Approval' },
  { type: 'APPROVAL_REQUEST', label: 'Approval Request' },
  { type: 'APPROVAL_RESULT', label: 'Approval Result' },
  { type: 'PO_APPROVAL_REQUEST', label: 'PO Approval' },
  { type: 'CREDIT_LIMIT', label: 'Credit Limit' },
  { type: 'TRIAL_EXPIRY', label: 'Trial Expiry' },
  { type: 'SYSTEM_ANNOUNCEMENT', label: 'System Announcement' },
];

const DEFAULT_ROW: Record<NotificationChannel, boolean> = {
  in_app: true,
  email: false,
  sms: false,
  whatsapp: false,
  push: false,
};

/**
 * Types whose push is on until turned off — mirrors the API's
 * PUSH_ON_BY_DEFAULT_TYPES (L04: a manager's purchase order waiting for you).
 */
const PUSH_ON_BY_DEFAULT_TYPES: readonly string[] = ['PO_APPROVAL_REQUEST'];

function defaultCell(type: NotificationType, channel: NotificationChannel): boolean {
  return channel === 'push' && PUSH_ON_BY_DEFAULT_TYPES.includes(type) ? true : DEFAULT_ROW[channel];
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface NotificationMatrixCardProps {
  globalPrefs: NotificationPreferences;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function NotificationMatrixCard({ globalPrefs }: NotificationMatrixCardProps) {
  const { toast } = useToast();
  const [rows, setRows] = useState<Record<string, NotificationPreferenceMatrixRow>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await notificationService.getMatrix();
      const byType: Record<string, NotificationPreferenceMatrixRow> = {};
      res.data.forEach((r) => {
        byType[r.notification_type] = r;
      });
      setRows(byType);
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to load matrix',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const cellState = useCallback(
    (type: NotificationType, channel: NotificationChannel): boolean => {
      const row = rows[type];
      if (!row) return defaultCell(type, channel);
      return row[channel];
    },
    [rows],
  );

  const handleToggle = useCallback(
    async (type: NotificationType, channel: NotificationChannel, next: boolean) => {
      const cellKey = `${type}:${channel}`;
      const previous = cellState(type, channel);
      // Optimistic update
      setRows((prev) => {
        const baseRow =
          prev[type] ?? {
            notification_type: type,
            in_app: DEFAULT_ROW.in_app,
            email: DEFAULT_ROW.email,
            sms: DEFAULT_ROW.sms,
            whatsapp: DEFAULT_ROW.whatsapp,
            push: defaultCell(type, 'push'),
            updated_at: '',
          };
        return { ...prev, [type]: { ...baseRow, [channel]: next } };
      });
      setSavingCell(cellKey);
      try {
        const updated = await notificationService.updateMatrixCell({
          notification_type: type,
          channel,
          enabled: next,
        });
        setRows((prev) => ({ ...prev, [type]: updated }));
      } catch (err) {
        // Revert
        setRows((prev) => {
          const row = prev[type];
          if (!row) return prev;
          return { ...prev, [type]: { ...row, [channel]: previous } };
        });
        toast({
          title: err instanceof ApiError ? err.message : 'Failed to save preference',
          variant: 'error',
        });
      } finally {
        setSavingCell((curr) => (curr === cellKey ? null : curr));
      }
    },
    [cellState, toast],
  );

  const handleReset = useCallback(async () => {
    setIsResetting(true);
    try {
      await notificationService.resetMatrix();
      setRows({});
      toast({ title: 'Matrix reset to defaults', variant: 'success' });
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to reset matrix',
        variant: 'error',
      });
    } finally {
      setIsResetting(false);
      setShowResetConfirm(false);
    }
  }, [toast]);

  const isColumnLocked = useCallback(
    (channel: ChannelMeta) => {
      if (!channel.globalKey) return false;
      return !globalPrefs[channel.globalKey];
    },
    [globalPrefs],
  );

  const hasCustomizations = useMemo(() => Object.keys(rows).length > 0, [rows]);

  return (
    <div className={cn(GLASS_FORM, 'p-5 sm:p-6 animate-slide-up')}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-sm font-bold text-neutral-800 dark:text-neutral-100 uppercase tracking-wider">
            Per-event overrides
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-prose">
            Fine-tune which delivery channels fire for each notification type. A channel column is
            locked when its master switch above is off.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowResetConfirm(true)}
          disabled={isLoading || !hasCustomizations}
        >
          Reset to defaults
        </Button>
      </div>

      {isLoading ? (
        <Skeleton variant="rect" height={320} className="rounded-xl" />
      ) : (
        <div className="overflow-x-auto rounded-xl ring-1 ring-inset ring-neutral-100 dark:ring-neutral-800/70">
          <table className="w-full text-left text-sm">
            <thead className="bg-white/40 dark:bg-neutral-900/40">
              <tr className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                <th className="px-4 py-3 sticky left-0 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm">
                  Notification
                </th>
                {CHANNELS.map((ch) => {
                  const locked = isColumnLocked(ch);
                  return (
                    <th key={ch.key} className="px-3 py-3 text-center">
                      <div className="inline-flex flex-col items-center gap-1">
                        <svg
                          className={cn('h-4 w-4', locked && 'opacity-40')}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d={ch.iconPath} />
                        </svg>
                        <span className={cn('text-[10px]', locked && 'text-neutral-400')}>
                          {ch.label}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100/70 dark:divide-neutral-800/80">
              {TYPE_ROWS.map(({ type, label }, idx) => (
                <tr
                  key={type}
                  className={cn(
                    'transition-colors',
                    idx % 2 === 0
                      ? 'bg-white/30 dark:bg-neutral-900/20'
                      : 'bg-transparent',
                    'hover:bg-brand-50/40 dark:hover:bg-brand-900/15',
                  )}
                >
                  <td className="px-4 py-2.5 text-sm font-semibold text-neutral-800 dark:text-neutral-100 whitespace-nowrap sticky left-0 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm">
                    {label}
                  </td>
                  {CHANNELS.map((ch) => {
                    const enabled = cellState(type, ch.key);
                    const locked = isColumnLocked(ch);
                    const saving = savingCell === `${type}:${ch.key}`;
                    return (
                      <td key={ch.key} className="px-3 py-2 text-center">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={enabled}
                          aria-label={`${label} — ${ch.label}`}
                          disabled={locked || saving || ch.key === 'in_app'}
                          onClick={() => handleToggle(type, ch.key, !enabled)}
                          className={cn(
                            'inline-flex h-6 w-6 items-center justify-center rounded-lg transition-all',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                            enabled && !locked
                              ? cn(TONE_ON[ch.tone], 'ring-1 ring-inset')
                              : 'bg-white/50 dark:bg-neutral-800/40 text-neutral-400 ring-1 ring-inset ring-neutral-200/60 dark:ring-neutral-700/50',
                            (locked || ch.key === 'in_app') && 'opacity-50 cursor-not-allowed',
                            !locked && ch.key !== 'in_app' && 'cursor-pointer hover:scale-105',
                          )}
                        >
                          {enabled ? (
                            <svg
                              className="h-3.5 w-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <span className="h-1 w-2.5 rounded-full bg-current opacity-50" />
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[11px] text-neutral-500 dark:text-neutral-400 leading-relaxed">
        In-app delivery is always on — the inbox is the source of truth. Expensive channels are
        opt-in; every cell is lazily created on first click.
      </p>

      <ConfirmDialog
        open={showResetConfirm}
        title="Reset per-event overrides?"
        description="This wipes every custom channel preference you've set for notification types. The defaults (in-app only, all other channels off) will be restored. This cannot be undone."
        confirmLabel={isResetting ? 'Resetting…' : 'Reset'}
        cancelLabel="Keep overrides"
        variant="danger"
        loading={isResetting}
        onConfirm={handleReset}
        onCancel={() => setShowResetConfirm(false)}
      />
    </div>
  );
}
